import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Button,
  Modal,
  Form,
  Alert,
  Tabs,
  Tab,
  InputGroup,
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaSearch, FaRobot } from 'react-icons/fa';

import { selectAuthUserId } from '../redux/authSlice';
import VideoCard from '../components/ui/VideoCard';
import FancyButton from '../components/ui/fancyButton';
import FormField from '../components/ui/FormField';
import TextInput from '../components/ui/TextInput';
import TextArea from '../components/ui/TextArea';
import Select from '../components/ui/Select';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import {
  fetchAllVideos,
  fetchAIRecommendedVideos,
  addVideo,
  updateVideo,
  deleteVideo,
  searchVideos,
} from '../services/supabaseClient/videoLibraryService';
import { fetchCoursesByUserId } from '../services/supabaseClient/courseService';
import './LibraryPage.css';

const LibraryPage = () => {
  const userId = useSelector(selectAuthUserId);
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [aiRecommendedVideos, setAiRecommendedVideos] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLibrarianModal, setShowLibrarianModal] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    video_url: '',
    title: '',
    description: '',
    course_id: '',
    tags: '',
    notes: '',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch videos and courses in parallel
      const [videosResult, aiVideosResult, coursesResult] = await Promise.all([
        fetchAllVideos(userId),
        fetchAIRecommendedVideos(userId),
        fetchCoursesByUserId(userId),
      ]);

      if (videosResult.error) throw videosResult.error;
      if (aiVideosResult.error) throw aiVideosResult.error;
      if (coursesResult.error) throw coursesResult.error;

      setVideos(videosResult.data || []);
      setAiRecommendedVideos(aiVideosResult.data || []);
      setCourses(coursesResult.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load library');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      loadData();
    }
  }, [userId, loadData]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadData();
      return;
    }

    setLoading(true);
    const { data, error: searchError } = await searchVideos(userId, searchTerm);
    setLoading(false);

    if (searchError) {
      setError(searchError.message);
    } else {
      setVideos(data || []);
    }
  };

  const handleAddVideo = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.course_id) {
      setError('Please select a course. All videos must be associated with a course.');
      return;
    }

    const tagsArray = formData.tags ? formData.tags.split(',').map((tag) => tag.trim()) : [];

    const videoData = {
      video_url: formData.video_url,
      title: formData.title,
      description: formData.description,
      course_id: formData.course_id,
      tags: tagsArray,
      notes: formData.notes,
    };

    const { error: addError } = await addVideo(userId, videoData);

    if (addError) {
      setError(addError.message);
    } else {
      setShowAddModal(false);
      resetForm();
      loadData();
    }
  };

  const handleEditVideo = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.course_id) {
      setError('Please select a course. All videos must be associated with a course.');
      return;
    }

    const tagsArray = formData.tags ? formData.tags.split(',').map((tag) => tag.trim()) : [];

    const updates = {
      title: formData.title,
      description: formData.description,
      course_id: formData.course_id,
      tags: tagsArray,
      notes: formData.notes,
    };

    const { error: updateError } = await updateVideo(currentVideo.id, updates);

    if (updateError) {
      setError(updateError.message);
    } else {
      setShowEditModal(false);
      resetForm();
      loadData();
    }
  };

  const handleDeleteVideo = async (video) => {
    if (window.confirm(`Are you sure you want to delete "${video.title}"?`)) {
      const { error: deleteError } = await deleteVideo(video.id);

      if (deleteError) {
        setError(deleteError.message);
      } else {
        loadData();
      }
    }
  };

  const handlePlayVideo = (video) => {
    // Navigate to split-screen watch page instead of modal
    navigate(`/video/${video.id}`);
  };

  const handleEditClick = (video) => {
    setCurrentVideo(video);
    setFormData({
      video_url: video.video_url,
      title: video.title,
      description: video.description || '',
      course_id: video.course_id || '',
      tags: video.tags ? video.tags.join(', ') : '',
      notes: video.notes || '',
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      video_url: '',
      title: '',
      description: '',
      course_id: '',
      tags: '',
      notes: '',
    });
    setCurrentVideo(null);
  };

  if (loading && videos.length === 0) {
    return (
      <Container className="library-page py-5">
        <LoadingSpinner />
      </Container>
    );
  }

  return (
    <Container className="library-page py-4">
      <div className="library-header mb-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <h2 className="library-title">
              <i className="fa-solid fa-book me-2" />
              Library
            </h2>
            <p className="text-muted">Manage your learning resources</p>
          </div>
          <div className="d-flex gap-2">
            <FancyButton variant="primary" onClick={() => setShowLibrarianModal(true)}>
              <FaRobot className="me-2" />
              Ask Librarian
            </FancyButton>
            <FancyButton variant="secondary" onClick={() => setShowAddModal(true)}>
              <FaPlus className="me-2" />
              Add Video
            </FancyButton>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="library-search mb-4">
        <InputGroup>
          <Form.Control
            type="text"
            placeholder="Search videos by title or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <Button variant="outline-primary" onClick={handleSearch}>
            <FaSearch />
          </Button>
        </InputGroup>
      </div>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4 library-tabs">
        <Tab eventKey="all" title={`All Videos (${videos.length})`}>
          {videos.length === 0 ? (
            <div className="empty-state text-center py-5">
              <i className="fa-solid fa-video fa-3x mb-3 text-muted" />
              <h4>No videos yet</h4>
              <p className="text-muted">Add your first video to start building your library</p>
              <FancyButton variant="primary" onClick={() => setShowAddModal(true)}>
                <FaPlus className="me-2" />
                Add Video
              </FancyButton>
            </div>
          ) : (
            <Row>
              {videos.map((video) => (
                <Col key={video.id} xs={12} sm={6} md={4} lg={3} className="mb-4">
                  <VideoCard
                    video={video}
                    onPlay={handlePlayVideo}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteVideo}
                  />
                </Col>
              ))}
            </Row>
          )}
        </Tab>

        <Tab eventKey="ai" title={`AI Recommended (${aiRecommendedVideos.length})`}>
          {aiRecommendedVideos.length === 0 ? (
            <div className="empty-state text-center py-5">
              <FaRobot size={48} className="mb-3 text-muted" />
              <h4>No AI recommendations yet</h4>
              <p className="text-muted">Ask the Librarian for personalized video recommendations</p>
              <FancyButton variant="primary" onClick={() => setShowLibrarianModal(true)}>
                <FaRobot className="me-2" />
                Ask Librarian
              </FancyButton>
            </div>
          ) : (
            <Row>
              {aiRecommendedVideos.map((video) => (
                <Col key={video.id} xs={12} sm={6} md={4} lg={3} className="mb-4">
                  <VideoCard
                    video={video}
                    onPlay={handlePlayVideo}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteVideo}
                  />
                </Col>
              ))}
            </Row>
          )}
        </Tab>
      </Tabs>

      {/* Add Video Modal */}
      <Modal
        show={showAddModal}
        onHide={() => {
          setShowAddModal(false);
          resetForm();
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Video to Library</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddVideo}>
            <FormField label="YouTube Video URL" htmlFor="video_url" required>
              <TextInput
                id="video_url"
                name="video_url"
                type="url"
                placeholder="https://www.youtube.com/watch?v=..."
                value={formData.video_url}
                onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                required
              />
            </FormField>

            <FormField label="Title" htmlFor="title" required>
              <TextInput
                id="title"
                name="title"
                placeholder="Video title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </FormField>

            <FormField label="Description" htmlFor="description">
              <TextArea
                id="description"
                name="description"
                rows={3}
                placeholder="Brief description of the video"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </FormField>

            <FormField
              label="Associate with Course"
              htmlFor="course_id"
              required
              helpText="All videos must be associated with a course"
            >
              <Select
                id="course_id"
                name="course_id"
                value={formData.course_id}
                onChange={(e) => setFormData({ ...formData, course_id: e.target.value })}
                options={[
                  { value: '', label: '-- Select a course (required) --' },
                  ...courses.map((course) => ({
                    value: course.id,
                    label: course.title,
                  })),
                ]}
                placeholder="Select a course"
                required
              />
            </FormField>

            <FormField label="Tags" htmlFor="tags" helpText="Comma-separated">
              <TextInput
                id="tags"
                name="tags"
                placeholder="e.g., python, machine learning, tutorial"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              />
            </FormField>

            <FormField label="Notes" htmlFor="notes">
              <TextArea
                id="notes"
                name="notes"
                rows={2}
                placeholder="Personal notes about this video"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </FormField>

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Add Video
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Video Modal */}
      <Modal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          resetForm();
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Video</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEditVideo}>
            <FormField label="Title" htmlFor="edit_title" required>
              <TextInput
                id="edit_title"
                name="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </FormField>

            <FormField label="Description" htmlFor="edit_description">
              <TextArea
                id="edit_description"
                name="description"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </FormField>

            <FormField label="Associate with Course" htmlFor="edit_course_id" required>
              <Select
                id="edit_course_id"
                name="course_id"
                value={formData.course_id}
                onChange={(e) => setFormData({ ...formData, course_id: e.target.value })}
                options={[
                  { value: '', label: '-- Select a course (required) --' },
                  ...courses.map((course) => ({
                    value: course.id,
                    label: course.title,
                  })),
                ]}
                required
              />
            </FormField>

            <FormField label="Tags" htmlFor="edit_tags" helpText="Comma-separated">
              <TextInput
                id="edit_tags"
                name="tags"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              />
            </FormField>

            <FormField label="Notes" htmlFor="edit_notes">
              <TextArea
                id="edit_notes"
                name="notes"
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </FormField>

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Save Changes
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Librarian Modal - Placeholder for AI integration */}
      <Modal
        show={showLibrarianModal}
        onHide={() => setShowLibrarianModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaRobot className="me-2" />
            Ask the Librarian
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <strong>Coming Soon!</strong>
          </Alert>
          <p className="text-muted">
            This feature will integrate with the existing Librarian component to suggest relevant
            YouTube videos with transcripts for your learning journey.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLibrarianModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LibraryPage;
